// Issue #1206: turn a userfile's processing failure into a chip label + tooltip
// lines. Same shape as missingCalibrationReason.js so the wording and colour
// choices stay in one place across the four file render sites.
//
// Before #1206 the server recorded only `processing_status: 'failed'` and no
// reason, and the GUI rendered neither — a user saw a file that silently never
// got a preview, with nothing anywhere to say why.

const MAX_ERROR_CHARS = 200;

// Keyed off processing_error_class, which the server assigns (see
// services/pipeline/processingFailure.ts). Deliberately plain language: the raw
// exception is shown underneath for anyone who wants it.
const CLASS_SENTENCES = {
    upload_failed:
        'Processing failed while saving the frame to storage, so the image data may not have been stored.',
    file_lost:
        'Processing failed because the source frame was no longer available to the pipeline.',
    job_stalled:
        'Processing was interrupted and the job exceeded its retry limit.',
    module_error:
        'A processing step failed for this file.',
    unknown:
        'Processing failed.',
};

/**
 * @param {object} file  UserFile from the /user-files endpoints — needs at least
 *   { processing_status, processing_error, processing_error_class, processing_failed_at }.
 * @returns {{ label: string, color: 'error'|'warning', tooltipLines: string[] } | null}
 *   null unless this file's processing actually failed.
 */
export function processingFailureReason(file) {
    if (!file) return null;
    if (file.processing_status !== 'failed') return null;

    const cls = file.processing_error_class || 'unknown';
    const tooltipLines = [CLASS_SENTENCES[cls] || CLASS_SENTENCES.unknown];

    if (file.processing_error) {
        let detail = String(file.processing_error);
        if (detail.length > MAX_ERROR_CHARS) {
            // Prod messages embed full buffer paths; keep the tooltip readable.
            detail = `${detail.slice(0, MAX_ERROR_CHARS)}…`;
        }
        tooltipLines.push('', detail);
    } else {
        // A failed file with no recorded reason is exactly the state #1206 fixes.
        // Rows that predate the fix (or that the backfill could not resolve) still
        // have to render — returning null here would reproduce the original bug.
        tooltipLines.push('', 'No reason was recorded for this failure.');
    }

    if (file.processing_failed_at) {
        const when = new Date(file.processing_failed_at);
        if (!Number.isNaN(when.getTime())) {
            tooltipLines.push('', `Failed ${when.toLocaleString()}`);
        }
    }

    return {
        label: 'Processing failed',
        // A lost source frame is worse than a step that can be re-run.
        color: cls === 'file_lost' ? 'error' : 'warning',
        tooltipLines,
    };
}

export default processingFailureReason;
